import React, { useState, useEffect } from "react";
import { message } from "antd";
import { fetchEmployees, fetchDepartments } from "Services/AdminServices/PerformanceService";
import generatePDFReport from "FileGenerator/AdminFileGenerator/PerformacePDFFile";
import { useNavigate } from "react-router-dom";

function usePerformance() {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getDepartmentsData();
  }, []);

  const fetchEmployeesData = async (departmentId) => {
    setLoading(true);
    try {
      const data = await fetchEmployees(departmentId);
      setEmployees(data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      message.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentsData = async () => {
    try {
      const data = await fetchDepartments();

      // Assuming your departments have id and name properties
      const departmentList = data.map((dept) => ({
        id: dept.id, // or whatever the ID field is
        name: dept.name || dept.Name, // adjust based on your API response
      }));

      setDepartments(departmentList);

      // Auto-select first department and fetch its employees
      if (departmentList.length > 0) {
        const firstDept = departmentList[0];
        setSelectedDepartment(firstDept.name);
        setSelectedDepartmentId(firstDept.id);
        fetchEmployeesData(firstDept.id);
      }
    } catch (error) {
      message.error("Failed to fetch departments");
    }
  };

  const handleDepartmentChange = (e) => {
    const selectedDeptName = e.target.value;
    setSelectedDepartment(selectedDeptName);

    // Find the department ID from the selected name
    const selectedDept = departments.find((dept) => dept.name === selectedDeptName);
    if (selectedDept) {
      setSelectedDepartmentId(selectedDept.id);
      fetchEmployeesData(selectedDept.id);
    }
  };

  const handleDownloadReport = () => {
    try {
      if (employees.length === 0) {
        message.warning("No employee data available to download");
        return;
      }

      generatePDFReport(employees, selectedDepartment);
      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download report");
    }
  };

  const handleViewDetails = (employee) => {
    navigate("/performance/detail", {
      state: {
        employeeData: employee,
      },
    });
  };

const columns = [
  {
    title: "Employee ID",
    dataIndex: "revieweeId",
    key: "revieweeId",
  },

  {
    title: "Employee Name",
    dataIndex: "revieweeName",
    key: "revieweeName",
  },

  {
    title: "Job Title",
    key: "jobTitle",
    render: (_, record) =>
      record.jobTitle ||
      record.jobTitleName ||
      record.revieweeJobTitle ||
      record.designationName ||
      record.designation ||
      "-",
  },

  {
    title: "Manager",
    dataIndex: "reviewerName",
    key: "reviewerName",
  },

  {
    title: "Performance Score",
    dataIndex: "totalPerformanceScore",
    key: "totalPerformanceScore",
    render: (score) =>
      score !== undefined && score !== null ? `${score}/100` : "N/A",
  },

  {
  title: "Rating",
  dataIndex: "performanceRatingName",
  key: "performanceRatingName",
  render: (rating) => {
    // Mapping to your professional color variables
    const getRatingStyles = (val) => {
      switch (val) {
        case "Far Exceeds Expectations":
          return { bg: "#dff7c0", text: "#226e1b", border: "#b7d89a" }; // Success/Primary
        case "Exceeds Expectations":
          return { bg: "#ecffc7", text: "#549247", border: "#b7d89a" }; // Badge-secondary
        case "Meets Expectations":
          return { bg: "#fef5d3", text: "#fbc400", border: "#fef1c2" }; // Warning
        case "Needs Improvement":
        case "Below Expectations":
          return { bg: "#fc9797", text: "#bd0000", border: "#f9b4b4" }; // Error
        default:
          return { bg: "#f6fbed", text: "#83b36d", border: "#e2e8f0" }; // Default/Empty
      }
    };

    const styles = getRatingStyles(rating);

    return (
      <span
        style={{
          backgroundColor: styles.bg,
          color: styles.text,
          border: `1px solid ${styles.border}`,
          padding: "4px 12px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: "600",
          display: "inline-block",
          textTransform: "uppercase",
          letterSpacing: "0.3px"
        }}
      >
        {rating || "No Rating"}
      </span>
    );
  },
},

  {
    title: "Review Period",
    key: "reviewPeriod",
    render: (_, record) => (
      <span>
        {record.periodStart
          ? new Date(record.periodStart).toLocaleDateString()
          : "N/A"}{" "}
        -{" "}
        {record.periodEnd
          ? new Date(record.periodEnd).toLocaleDateString()
          : "N/A"}
      </span>
    ),
  },

  {
    title: "Actions",
    key: "actions",
    render: (_, record) => (
      <button
        type="button"
        className="btn-back"
        onClick={() => handleViewDetails(record)}
      >
        View Details
      </button>
    ),
  },
];

  return {
    departments: departments.map((dept) => dept.name), // Only pass names to the component
    loading,
    handleDepartmentChange,
    handleDownloadReport,
    columns,
    selectedDepartment,
    employees,
    handleViewDetails,
  };
}

export default usePerformance;
